// Content for the temple campaign detail page.
//
// The campaign gives the title, photos, money raised, documents, updates, donors and comments.
// The temple-only parts (Hindi tagline, subtitle, chips, the four facts, the history eras and
// the story chapters) come from admin → Temples → Temple Detail Pages
// (GET /temple-details/:campaignId). Anything left empty there is hidden on the page.
// The Story section shows only the temple page's story chapters (admin → Temple Page → Story).
// The campaign's Project Descriptions are not used on temple pages.

const FACT_KEYS = ["managed", "age", "devotees", "status"];
const FACT_LABELS = { managed: "Managed By", age: "Age", devotees: "Devotees", status: "Status" };

const isImageUrl = (u) => typeof u === "string" && u.trim() !== "" && !/\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(u);
const text = (v) => (typeof v === "string" ? v.trim() : "");

// Every photo the campaign has, banner first — eras and chapters without their own photo use these.
const campaignPhotos = (campaign) => {
  const out = [];
  const add = (u) => {
    if (isImageUrl(u) && !out.includes(u)) out.push(u);
  };
  add(campaign?.bannerImage);
  add(campaign?.image);
  (campaign?.projects || []).forEach((p) => add(p?.image));
  (campaign?.updates || []).forEach((u) => (Array.isArray(u?.images) ? u.images : [u?.image]).forEach(add));
  return out;
};

export const buildTempleDetail = (campaign, detail) => {
  const d = detail || {};
  const photos = campaignPhotos(campaign);
  const photoAt = (i) => (photos.length ? photos[i % photos.length] : "");
  const raised = Number(campaign?.raisedAmount) || 0;
  const goal = Number(campaign?.goalAmount) || 0;

  const facts = FACT_KEYS.map((key) => {
    const f = (Array.isArray(d.facts) ? d.facts : []).find((x) => x?.key === key) || {};
    return { key, label: text(f.label) || FACT_LABELS[key], value: text(f.value) };
  }).filter((f) => f.value);

  const eras = (Array.isArray(d.eras) ? d.eras : [])
    .filter((e) => text(e?.year) || text(e?.title))
    .map((e, i) => ({
      tag: text(e.tag),
      year: text(e.year),
      caption: text(e.caption) || [text(e.year), text(e.title)].filter(Boolean).join(" · "),
      title: text(e.title),
      body: text(e.body),
      accent: Boolean(e.accent),
      image: e.image_url || photoAt(i),
    }));

  // A story chapter without its own photo uses the temple's uploaded history photos first (chapter
  // N → era N's photo, wrapping), and the campaign's photos only when no era has one.
  const eraPhotos = (Array.isArray(d.eras) ? d.eras : []).map((e) => e?.image_url).filter(Boolean);
  const slides = (Array.isArray(d.slides) ? d.slides : [])
    .filter((s) => text(s?.title))
    .map((s, i) => ({
      tag: text(s.tag),
      title: text(s.title),
      body: text(s.body),
      cta: text(s.cta),
      image: s.image_url || (eraPhotos.length ? eraPhotos[i % eraPhotos.length] : photoAt(i + 1)),
    }));

  const title = campaign?.title || "";
  return {
    id: campaign?.id,
    title,
    // Big heading on the temple page: the temple's own name (admin → Temple Page → Temple name);
    // the campaign title stays the fundraising headline used on cards and in the donation flow.
    name: text(d.temple_name) || title,
    tagline: text(d.tagline),
    // Line under the temple name = the campaign title (one fundraising message for the page,
    // cards and lists). Without a temple name the campaign title is the heading, so the older
    // separate subtitle is shown instead.
    subtitle: text(d.temple_name) ? title : text(d.subtitle),
    heroImage: photoAt(0),
    chips: [
      { icon: "location", label: text(d.location) || text(campaign?.location) },
      { icon: "temple", label: text(d.built_label) },
    ].filter((c) => c.label),
    facts,
    raised,
    goal,
    backers: Number(campaign?.backers) || 0,
    pct: goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0,
    // Admin → Campaign Management: Hide Goal / Hide Raised (raised amount + donors) / Hide All (progress)
    showGoal: Number(campaign?.hide_goal) !== 1,
    showRaised: Number(campaign?.hide_raised) !== 1,
    showProgress: Number(campaign?.hideAll) !== 1,
    taxBenefit: campaign?.taxBenefit || "",
    history: { title: text(d.history_title) || "The history of this temple", eras },
    story: { title: text(d.story_title) || "The story", slides },
  };
};
